import { persistReducer } from 'redux-persist'
import hardSet from 'redux-persist/es/stateReconciler/hardSet'
// import hardSet from 'redux-persist/lib/stateReconciler/hardSet'
import storage from 'redux-persist/lib/storage/session'
import { ActivationAgreementModel } from '../../Libs/home-power-station/models/home-power-station.model'

import { LiveDataModel, PowermeterHardwareModel, PowermeterHardwareDataModel, StorageDataModel, StorageLocation, StorageAction } from './StorageModel'
import { PowermeterLiveDataModel } from 'src/Libs/power-meters'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { isEqual } from 'lodash'

export interface StorageReducerState {
  installations: Installation[]
  powermeters?: PowermeterHardwareDataModel
  liveData?: LiveDataModel
  activeStorage: string | undefined
  activeStorageBattery: string | undefined //hager systems have active storage != sn of the battery
  activeInstallation: number | undefined
  powermeterHardware: PowermeterHardwareDataModel
  location?: StorageLocation
  storageData?: StorageDataModel
  storageLoading: boolean
  warranty?: ActivationAgreementModel
  storageColor: string
  installation?: Installation
  storeInitialised?: boolean
}

const initialState: StorageReducerState = {
  installations: [],
  powermeters: undefined,
  liveData: undefined,
  activeStorage: undefined,
  activeStorageBattery: undefined,
  activeInstallation: undefined,
  powermeterHardware: {
    consumers: [],
    producers: [],
    bypassRegulation: [],
    bypassNoCharge: [],
    enera: [],
    hvac: [],
  },
  location: undefined,
  storageData: undefined,
  storageLoading: false,
  warranty: undefined,
  storageColor: '',
  installation: undefined,
  storeInitialised: false,
}

const sumPmHardwareValues = (pmHardwareValues: PowermeterHardwareModel[]): number => {
  return pmHardwareValues.reduce((prev, next) => {
    if (!next.power) return prev
    return prev + next.power
  }, 0)
}
const storageReducer = (state: StorageReducerState = initialState, action: StorageAction): StorageReducerState => {
  switch (action.type) {
    case 'SET_INSTALLATIONS':
      if (!action.installations || isEqual(state.installations, action.installations)) return state
      return {
        ...state,
        installations: action.installations ?? state.installations,
      }
    case 'SET_STORAGE_LOADING':
      if (isEqual(state.storageLoading, action.storageLoading)) return state

      return {
        ...state,
        storageLoading: action.storageLoading ?? state.storageLoading,
      }
    case 'SET_POWERMETERS': {
      if (!action.powermeterHardware) return state
      const sortedPowermeters = sortPowermeters(action.powermeterHardware)
      if (isEqual(state.powermeterHardware, sortedPowermeters)) return state

      return {
        ...state,
        powermeterHardware: action.powermeterHardware ? sortPowermeters(action.powermeterHardware) : state.powermeterHardware,
      }
    }
    case 'SET_POWERMETER_LIVE_DATA': {
      // the liveData musst be set via ADD_LIVE_DATA
      if (!action.powermeterLiveData || !state.liveData) return state

      const mergedValues = mergePowermeterDataWithTypes(state.powermeterHardware ?? [], action.powermeterLiveData)

      return {
        ...state,
        liveData: {
          ...state.liveData,
          powermeters: mergedValues,
          homeConsumerPower: sumPmHardwareValues(mergedValues.consumers),
          trackerHomePower: sumPmHardwareValues([...mergedValues.producers, ...mergedValues.bypassNoCharge]),
        },
      }
    }
    case 'ADD_LIVE_DATA':
      if (!action.liveData || isEqual(state.liveData, action.liveData)) return state

      return {
        ...state,
        liveData: action.liveData ?? state.liveData,
      }
    case 'SET_ACTIVE_STORAGE': {
      if (isEqual(state.activeStorage, action.activeStorage)) return state

      // Nullable storageIds are used to overwrite the store (construction-installation) and undefined Ids default to the prev. state-Id
      let activeStorageToSave: string | undefined

      if (action.activeStorage === null) {
        activeStorageToSave = undefined
      } else if (Number.isNaN(Number(action.activeStorage))) {
        activeStorageToSave = state.activeStorage
      } else {
        activeStorageToSave = action.activeStorage
      }

      return {
        ...state,
        activeStorage: activeStorageToSave,
      }
    }
    case 'SET_ACTIVE_STORAGE_BATTERY':
      if (isEqual(state.activeStorageBattery, action.activeStorageBattery)) return state

      return {
        ...state,
        activeStorageBattery: action.activeStorageBattery,
      }
    case 'SET_INSTALLATION':
      if (!action.installation || isEqual(state.installation, action.installation)) return state

      return {
        ...state,
        installation: action.installation ?? state.installation,
      }
    case 'CLEAR_INSTALLATION':
      if (!state.installation) return state

      return {
        ...state,
        installation: undefined,
      }
    case 'SET_ACTIVE_INSTALLATION': {
      // Nullable storageIds are used to overwrite the store (construction-installation) and undefined Ids default to the prev. state-Id
      let activeStorageToSave: string | undefined

      if (action.activeStorage === null) {
        activeStorageToSave = undefined
      } else if (Number.isNaN(Number(action.activeStorage))) {
        // keep storage only if the installation is unchanged
        activeStorageToSave = isEqual(state.activeInstallation, action.activeInstallation) ? state.activeStorage : undefined
      } else {
        activeStorageToSave = action.activeStorage
      }

      if (
        action.activeInstallation === undefined ||
        (isEqual(state.activeInstallation, action.activeInstallation) && isEqual(state.activeStorage, activeStorageToSave))
      ) {
        return state
      }

      return {
        ...state,
        activeInstallation: action.activeInstallation ?? state.activeInstallation,
        activeStorage: activeStorageToSave,
      }
    }
    case 'SET_LOCATION':
      if (!action.location || isEqual(state.location, action.location)) return state

      return {
        ...state,
        location: action.location ?? state.location,
      }
    case 'SET_STORAGE_DATA':
      if (!action.storageData || isEqual(state.storageData, action.storageData)) return state

      return {
        ...state,
        storageData: action.storageData ?? state.storageData,
      }
    case 'SET_WARRANTY':
      if (!action.warranty || isEqual(state.warranty, action.warranty)) return state

      return {
        ...state,
        warranty: action.warranty ?? state.warranty,
      }
    case 'ADD_BATTERIES':
      if (!action.battery || !state.storageData) {
        return state
      }

      return {
        ...state,
        storageData: { ...state.storageData, battery: action.battery },
      }
    case 'SET_STORAGE_COLOR':
      if (isEqual(state.storageColor, action.storageColor)) return state

      return {
        ...state,
        storageColor: action.storageColor ?? state.storageColor,
      }
    case 'SET_STORE_INITIALISED':
      return {
        ...state,
        storeInitialised: true,
      }
    case 'RESET_ACTIVE_VALUES_TO_INITIAL': {
      const { activeStorage, activeInstallation, activeStorageBattery } = initialState

      return {
        ...state,
        activeStorage,
        activeInstallation,
        activeStorageBattery,
      }
    }
    default:
      return state
  }
}

// const getDevicesWithName = (deviceList: WallboxHardware[], deviceData: WallboxData[]): WallboxData[] => {
//   const devices = deviceData.map(device => {
//     const matchingDevice = deviceList.find(wallBox => wallBox.id === device.id)
//     if (matchingDevice) {
//       return Object.assign(device, { name: matchingDevice.DEVICE_NAME[1] })
//     }

//     return device
//   })

//   return devices
// }

export const sumPowermeterValues = (values: number[], isProducers?: boolean): number => {
  if (isProducers) {
    return values.filter(value => value <= 0).reduce((sum, value) => sum + value, 0)
  }
  return values.filter(value => value >= 0).reduce((sum, value) => sum + value, 0)
}

const mergePowermeterArray = (
  array: PowermeterHardwareModel[] | undefined,
  liveData: PowermeterLiveDataModel[],
  isProducers?: boolean
): PowermeterHardwareModel[] => {
  if (!array) return []

  return array.map(powermeter => {
    const matching = liveData.find(pmd => pmd?.deviceId === powermeter?.deviceID?.[1].toString())

    if (matching) {
      return Object.assign(powermeter, {
        power: sumPowermeterValues([matching.powerL1 ?? 0, matching.powerL2 ?? 0, matching.powerL3 ?? 0], isProducers),
      })
    }

    return powermeter
  })
}

const mergePowermeterDataWithTypes = (
  powermeterHardware: PowermeterHardwareDataModel,
  powermeterData: PowermeterLiveDataModel[]
): PowermeterHardwareDataModel => {
  const consumers = mergePowermeterArray(powermeterHardware.consumers, powermeterData)

  const producers = mergePowermeterArray(powermeterHardware.producers, powermeterData, true)
  const bypassNoCharge = mergePowermeterArray(powermeterHardware.bypassNoCharge, powermeterData, true)
  const bypassRegulation = mergePowermeterArray(powermeterHardware.bypassRegulation, powermeterData)
  const enera = mergePowermeterArray(powermeterHardware.enera, powermeterData)
  const hvac = mergePowermeterArray(powermeterHardware.hvac, powermeterData)

  return { consumers, producers, bypassRegulation, bypassNoCharge, enera, hvac }
}

/**
 * Sort powermeters into producers and consumers by type
 */
const sortPowermeters = (powermeterHardware: PowermeterHardwareModel[]): PowermeterHardwareDataModel => {
  const ordered = { consumers: [], producers: [], bypassRegulation: [], bypassNoCharge: [], enera: [], hvac: [] } as PowermeterHardwareDataModel

  powermeterHardware.forEach(element => {
    if (!element.type) return

    if (element.type[1] === 2) {
      ordered.producers.push(element)
    } else if (element.type[1] === 4) {
      ordered.consumers.push(element)
    } else if (element.type[1] === 3) {
      ordered.producers.push(element)
      ordered.consumers.push(element)
    } else if (element.type[1] === 10) {
      ordered.bypassRegulation.push(element)
    } else if (element.type[1] === 11) {
      ordered.bypassNoCharge.push(element)
    } else if (element.type[1] === 200) {
      ordered.enera.push(element)
    } else if (element.type[1] === 13) {
      ordered.hvac.push(element)
    }
  })

  return ordered
}

const persistConfig = {
  key: 'storage',
  storage: storage,
  whitelist: ['installations', 'activeStorage', 'activeInstallation', 'powermeterHardware'],
  stateReconciler: hardSet,
}

export default persistReducer(persistConfig, storageReducer)
